import {useState, useCallback} from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatDateTime } from '~/modules/utils';
import {useGetAutoMatchQuestionsQuery, useQueueAutoPopulateMutation, useQueueAutoPublishMutation, useGetConfigQuery} from '~/store/rtkQuery';
import { IS_DEV_MODE } from '~/modules/config';

import { QuestionCard } from './QuestionCard';

export const QuestionAutoMatches = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 0;
  });
  
  const [pageSize, setPageSize] = useState(() => {
    const pageSizeParam = searchParams.get('pageSize');
    return pageSizeParam ? parseInt(pageSizeParam, 10) : 20;
  });
  
  const [prodRunMatchType, setProdRunMatchType] = useState<string | undefined>(() => {
    const matchTypeParam = searchParams.get('matchType');
    return matchTypeParam || undefined;
  });

  const {
    data,
    isLoading,
    error: fetchError,
    refetch,
  } = useGetAutoMatchQuestionsQuery({page, pageSize, prodRunMatchType: prodRunMatchType as any});

  const { data: configData } = useGetConfigQuery();

  const [queueAutoPopulate, { isLoading: isQueueingPopulate }] = useQueueAutoPopulateMutation();
  const [queueAutoPublish, { isLoading: isQueueingPublish }] = useQueueAutoPublishMutation();

  // Helper function to update URL params
  const updateUrlParams = useCallback((updates: { page?: number; pageSize?: number; matchType?: string | undefined }) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (updates.page !== undefined) {
      if (updates.page === 0) {
        newSearchParams.delete('page');
      } else {
        newSearchParams.set('page', updates.page.toString());
      }
    }
    
    if (updates.pageSize !== undefined) {
      if (updates.pageSize === 20) {
        newSearchParams.delete('pageSize');
      } else {
        newSearchParams.set('pageSize', updates.pageSize.toString());
      }
    }
    
    if (updates.matchType !== undefined) {
      if (updates.matchType === undefined) {
        newSearchParams.delete('matchType');
      } else {
        newSearchParams.set('matchType', updates.matchType);
      }
    }
    
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrlParams({ page: newPage });
  }, [updateUrlParams]);

  const onPageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
    updateUrlParams({ pageSize: newPageSize, page: 0 });
  }, [updateUrlParams]);

  const onMatchTypeChange = useCallback((newMatchType: string) => {
    const matchTypeValue = newMatchType === 'all' ? undefined : newMatchType;
    setProdRunMatchType(matchTypeValue);
    setPage(0);
    updateUrlParams({ matchType: matchTypeValue, page: 0 });
  }, [updateUrlParams]);

  const handleQueueAutoPopulate = useCallback(async (questionId: string) => {
    try {
      await queueAutoPopulate({ datocmsQuestionId: questionId }).unwrap();
      // Optionally show success message or refetch data
      refetch();
    } catch (error) {
      console.error('Failed to queue auto-populate:', error);
      // Optionally show error message
    }
  }, [queueAutoPopulate, refetch]);

  const handleQueueAutoPublish = useCallback(async (questionId: string, questionVersion: string, matchId: string) => {
    try {
      await queueAutoPublish({ datocmsQuestionId: questionId, datocmsQuestionVersion: questionVersion, matchId, dryRun: IS_DEV_MODE }).unwrap();
      // Optionally show success message or refetch data
      refetch();
    } catch (error) {
      console.error('Failed to queue auto-publish:', error);
      // Optionally show error message
    }
  }, [queueAutoPublish, refetch]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-10">
        <span className="loading loading-dots loading-lg"></span>
        <p>Loading</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div role="alert" className="alert alert-error my-4">
        <span>{fetchError.message}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div role="alert" className="alert alert-warning my-4">
        <span>No data available</span>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Auto Publisher Status Banner */}
      <div className="mb-6">
        <div className={`alert ${configData?.sysAdminConfig.askLunaAutoPublish?.enabled ? 'alert-info' : 'alert-warning'}`}>
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col gap-1">
              <span className={`badge ${configData?.sysAdminConfig.askLunaAutoPublish?.enabled ? 'badge-success' : 'badge-error'}`}>
                Auto-Publisher {configData?.sysAdminConfig.askLunaAutoPublish?.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <span className="text-xs text-gray-600">
                {configData?.sysAdminConfig.askLunaAutoPublish?.lastRun ? `Last Run: ${formatDateTime(configData?.sysAdminConfig.askLunaAutoPublish?.lastRun)}` : 'Never run'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-4">
        <h1 className="text-2xl font-bold">Question Auto Matches</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm">Match Type:</label>
            <select
              className="select select-bordered select-sm"
              value={prodRunMatchType || 'all'}
              onChange={(e) => onMatchTypeChange(e.target.value)}>
              <option value="all">All</option>
              <option value="PERFECT_PUBLISH">Perfect Publish</option>
              <option value="FALSE_POSITIVE">False Positive</option>
              <option value="MISSED_OPPORTUNITY">Missed Opportunity</option>
              <option value="SAFELY_CAUGHT">Safely Caught</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm">Page Size:</label>
            <select
              className="select select-bordered select-sm"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={1000}>1000</option>
            </select>
          </div>
        </div>
      </div>


      <div className="grid gap-4">
        {data.questions.map((question) => (
          <QuestionCard
            key={question.question_id}
            question={question}
            onQueueAutoPopulate={handleQueueAutoPopulate}
            onQueueAutoPublish={handleQueueAutoPublish}
            isQueueingPopulate={isQueueingPopulate}
            isQueueingPublish={isQueueingPublish}
          />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, data.questions.length)} of {data.questions.length} questions
        </div>
        <div className="join">
          <button
            className="join-item btn btn-sm"
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}>
            «
          </button>
          <button className="join-item btn btn-sm">
            Page {page + 1}
          </button>
          <button
            className="join-item btn btn-sm"
            disabled={data.questions.length < pageSize}
            onClick={() => onPageChange(page + 1)}>
            »
          </button>
        </div>
      </div>

      {/* Floating Refresh Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button 
          className="btn btn-circle btn-primary shadow-lg hover:shadow-xl transition-shadow"
          onClick={() => refetch()}
          title="Refresh data"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
